import React from "react";
import Helmet from "react-helmet";
import { Link } from "react-router-dom";
import { Navbar2, Footer2 } from "../../components";
import SeriesNav from "../../components/labs/SeriesNav";
import { getRouteMeta } from "../../seoData";

function DigitalForensicsHomelabSysvol() {
  const { title, description: desc, image, url } = getRouteMeta(
    "/digital-forensics-homelab-sysvol"
  );

  return (
    <div>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} data-react-helmet="true"></meta>

        <meta property="og:type" content="website" data-react-helmet="true"></meta>
        <meta property="og:url" content={url} data-react-helmet="true"></meta>
        <meta property="og:title" content={title} data-react-helmet="true"></meta>
        <meta property="og:description" content={desc} data-react-helmet="true"></meta>
        <meta property="og:image" content={image} data-react-helmet="true"></meta>

        <meta property="twitter:card" content="summary_large_image"></meta>
        <meta property="twitter:url" content={url} data-react-helmet="true"></meta>
        <meta property="twitter:title" content={title} data-react-helmet="true"></meta>
        <meta property="twitter:description" content={desc} data-react-helmet="true"></meta>
        <meta property="twitter:image" content={image} data-react-helmet="true"></meta>
      </Helmet>
      <Navbar2 />
      <section id="blogs">
        <div className="blogs main">
          <div className="blogs-header pb-4">
            <Link to="/projects/" className="blog-back-link">
              <i className="fa-solid fa-arrow-left"></i>&nbsp;Back to Projects
            </Link>
            <h2 className="section-title">Digital Forensics HomeLab</h2>
            <div className="blog-meta">
              <span>
                <i className="fa-solid fa-share-nodes"></i>Part 4: SYSVOL
              </span>
            </div>
          </div>

          <SeriesNav current="/digital-forensics-homelab-sysvol/" />

          <p className="m-5">
            I didn't learn about SYSVOL from a course or a lab exercise
            first. I learned it at work, during an incident where part of
            cleanup meant figuring out what had changed inside it and how
            that change explained the lateral spread we were seeing.
          </p>

          <h3>What Is SYSVOL</h3>
          <p className="m-5">
            SYSVOL is a shared folder that exists on every domain
            controller, replicated between them by DFS Replication (the
            DFSR service from Part 3). On my DC it's shared out at{" "}
            <span className="fw-bolder">
              \\zingnet.local\SYSVOL\zingnet.local\
            </span>
            . It holds domain-wide policy and logon script data that every
            domain-joined machine needs to be able to reach, things like
            Group Policy templates and logon scripts, which is exactly
            what I'm using it for in Part 5.
          </p>

          <h3>The Role It Plays</h3>
          <p className="m-5">
            Because SYSVOL is what actually delivers Group Policy settings
            and scripts out to every machine in the domain, it's one of
            the most trusted paths in the whole environment. A domain-joined
            computer will pull down and run whatever's sitting in the right
            SYSVOL location without asking many questions, that's the
            entire point of it. Trusted-by-default and reachable
            domain-wide is a useful combination for legitimate
            administration, and a dangerous one if it's abused.
          </p>

          <h3>Why DCs Are Prime Targets for Attackers</h3>
          <p className="m-5">
            That's the part that clicked for me during the incident. A
            domain controller isn't just another server, it's the thing
            every other machine in the domain trusts by default. Get a
            foothold on a DC and you don't need to compromise machines one
            at a time, you have a direct line to something almost every
            endpoint will act on without hesitation. That's what makes DCs
            such high-value targets, and it's also why how exposed a DC is
            matters so much. If a DC is reachable the same way as any other
            box on the network, the blast radius of compromising it is
            enormous.
          </p>

          <h3>Using SYSVOL for Lateral Movement</h3>
          <p className="m-5">
            This is the piece the incident was actually about. If an
            attacker can write into SYSVOL, whether that's a logon script,
            a scheduled task definition, or a Group Policy Preference, that
            content will get pulled and executed by every machine the
            policy applies to the next time they check in. One write turns
            into execution across however many endpoints are in scope. It's
            not a flashy exploit, it's abusing exactly the trust
            relationship SYSVOL is supposed to have. Recognizing that
            during cleanup was what took the incident from "a machine is
            infected" to "this could spread to everything domain-joined if
            we don't handle SYSVOL specifically."
          </p>

          <h3>A Note on My Own Lab</h3>
          <p className="m-5">
            My DC isn't proxied yet, and to be specific about what that
            means: it isn't reachable from the internet right now because
            nothing's forwarded to it, pfSense just isn't set up to let
            anything in from outside. That's different from actually
            guaranteeing it stays that way. The goal is that the DC should
            never be directly reachable from the internet, not by
            accident, not for convenience, if I ever need remote access
            into the lab, that should go through a proxy sitting in front
            of it, not a port forwarded straight at the domain controller.
            Right now that's true because I haven't set up remote access at
            all, not because I've built anything that enforces it. Actually
            putting that proxy in place before I ever open the lab up
            remotely is on the list for a future part of this series.
          </p>

          <SeriesNav current="/digital-forensics-homelab-sysvol/" />
        </div>
        <Footer2 />
      </section>
    </div>
  );
}

export default DigitalForensicsHomelabSysvol;
